import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Table } from '@getsynapse/design-system';
import { TaskDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import { SortingType } from 'utils/customTypes';
import { orderBy } from 'lodash';
import { TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS } from 'utils/constants/dashboard';
import TaskDrillDownTableHeader from './TaskDrillDownTableHeader/TaskDrillDownTableHeader';
import TaskDrillDownTableBody from './TaskDrillDownTableBody/TaskDrillDownTableBody';
import { useSelector } from 'react-redux';
import drilldownSelectors from 'state/normalized/insights/drilldownSelectors';
import DrillTableHeaderFilters from 'Pages/DashboardsDetailsPage/components/DrillTableHeaderFilters';
import { handleTaskExportData } from 'Pages/DashboardsDetailsPage/components/DrillDownSidePanel/drillDownDataExportHandlers';
import { insightDrilldownMetadataSelectors } from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import TableSkeletonLoader from 'Organisms/TableSkeletonLoader/TableSkeletonLoader';

const columnMap = new Map<string, string>([
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NUMBER, 'task_number.value'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NAME, 'task_name'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STATUS, 'task_status'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE, 'task_type'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT, 'projects'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS, 'programs'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ASSIGNEES, 'owners'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.START_DATE, 'creation_date'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DUE_DATE, 'due_date'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DEMAND_EST_HOURS, 'demand_est_hours'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS, 'allocated_hours'],
  [TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS, 'actual_hours'],
]);

const TaskDrillDownTable = () => {
  const items = useSelector(drilldownSelectors.selectActiveDrilldownTasks);
  const cardName = useSelector(
    drilldownSelectors.selectActiveDrilldownCardName
  );
  const isLoading = useSelector(
    insightDrilldownMetadataSelectors.selectIsActiveDrilldownLoading
  );
  const exportItems = () => {
    handleTaskExportData(items);
  };
  const [orderByParam, setOrderByParam] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const orderByTaskTableData = (
    items: TaskDrillDownItem[],
    column: string,
    order: SortingType
  ) => {
    if (!items || items.length === 0) return [];

    const fieldName = columnMap.get(column) || '';
    const finalData = orderBy(items, [fieldName], [order]);
    return finalData;
  };

  const sortedItems = useMemo(() => {
    return orderByTaskTableData(items, orderByParam, order);
  }, [items, orderByParam, order]);

  const handleSort = useCallback(
    (column: string) => {
      const isSameColumn = column === orderByParam;
      const newOrder = isSameColumn && order === 'asc' ? 'desc' : 'asc';
      setOrderByParam(column);
      setOrder(newOrder);
    },
    [orderByParam, order]
  );

  return (
    <div>
      <DrillTableHeaderFilters
        title={cardName}
        onExportClick={exportItems}
        exportEnabled
      />
      <div
        className={classnames(
          'w-full rounded-b overflow-auto border border-neutral-lighter-two max-h-filtered-table-body'
        )}
      >
        {isLoading && <TableSkeletonLoader />}
        {!isLoading && (
          <Table
            className={classnames('relative', {
              'max-w-full border-0 w-full': items.length,
              'w-full': !items.length,
            })}
            canSelectRows={false}
            data={{
              headData: {
                stickyHeader: true,
                handleSort,
                headCells: [],
              },
              rows: [],
              total: items.length,
            }}
            data-cy='team-dashboards-table'
          >
            <TaskDrillDownTableHeader handleSort={handleSort} />
            <TaskDrillDownTableBody items={sortedItems} />
          </Table>
        )}
      </div>
    </div>
  );
};

export default TaskDrillDownTable;
